import NoHeaderLayout from "@/layouts/NoHeaderLayout";
import publicPath from "@/utils/publicPath";
import { NextSeo } from "next-seo";
import Link from "next/link";
import AnchorLink from "react-anchor-link-smooth-scroll";
import useMultiImageLoader from "@/hooks/useMultiImageLoader";
import useDevice from "@/hooks/useDevice";
import MenuButton from "@/pages/AdMakerPage/components/MenuButton";
import AnimeFadeIn from "@/pages/AdMakerPage/components/AnimeFadeIn";
import CardItem from "@/pages/AdMakerPage/components/CardItem";
import MakerLogoCarousel from "@/pages/AdMakerPage/components/MakerLogoCarousel";
import SectionTitle from "@/pages/AdMakerPage/components/SectionTitle";
import SampleImageFadeCaoursel from "@/pages/AdMakerPage/components/SampleImageFadeCarousel";
import FunctionItems from "@/pages/AdMakerPage/components/FunctionItems";
import SnsItem from "@/pages/AdMakerPage/components/SnsItem";
import { theme } from "tailwind.config";
import { PUBLIC_URL } from "@/config/env";
import usePageView from "@/hooks/usePageView";

const DOWNLOAD_URL =
  "https://tecture-20816776.hs-sites.com/about-tecture/download";
const ONLINE_MTG_URL = "https://tecture-20816776.hs-sites.com/meet-tecture";
const CONTACT_URL = "https://share.hsforms.com/14YcrUmuMTxqBv1g075w70Qce6bs";

const INSTAGRAM_URL = "https://www.instagram.com/tecture_official/";
const X_URL = "https://twitter.com/tecture1";
const FACEBOOK_URL = "https://www.facebook.com/tecture1/";

const MAG_URL = "https://mag.tecture.jp/";

const TOP_IMAGES = [
  "/imgs/maker_lp/TopImage01.webp",
  "/imgs/maker_lp/TopImage02.webp",
  "/imgs/maker_lp/TopImage03.webp",
  "/imgs/maker_lp/TopImage04.webp",
  "/imgs/maker_lp/TopImage05.webp",
  "/imgs/maker_lp/TopImage06.webp",
  "/imgs/maker_lp/TopImage07.webp",
];

const AdMakerPage = (): JSX.Element => {
  usePageView();

  const { isPc, isSp } = useDevice();

  // トップの画像を読み込む
  const { isComplete } = useMultiImageLoader(TOP_IMAGES);

  return (
    <>
      <NoHeaderLayout>
        <NextSeo
          title="TECTURE | 設計者の探すを軸にスペックインを加速する"
          description="あなたの建材をわたしたちのデータベースに登録しませんか？"
          canonical={publicPath("/ads/maker")}
          openGraph={{
            url: publicPath("/ads/maker"),
            images: [{ url: `${PUBLIC_URL}/imgs/maker_lp/ogp.png` }],
            site_name: "TECTURE",
            locale: "ja_JP",
            type: "website",
          }}
        />
        <div
          className="relative tracking-wider"
          style={{
            color: theme.colors.gray[700],
            marginTop: 1,
          }}
        >
          <header className="sticky inset-x-0 top-0 left-0 p-16 laptop:p-32 flex justify-between items-center z-50 bg-white">
            <Link href="/">
              <img
                width={isPc ? 147 : 160}
                src="/imgs/maker_lp/logo.svg"
                alt="TECTURE"
              />
            </Link>

            {isSp && (
              <MenuButton
                facebookURL={FACEBOOK_URL}
                instagramURL={INSTAGRAM_URL}
                xURL={X_URL}
              />
            )}

            {isPc && (
              <nav className="flex items-center gap-[40px]">
                <ul className="flex gap-32 font-bold">
                  <li>
                    <AnchorLink href="#merits">導入メリット</AnchorLink>
                  </li>

                  <li>
                    <AnchorLink href="#features">機能</AnchorLink>
                  </li>
                </ul>
                <ul className="flex items-center font-bold gap-20">
                  <li>
                    <Link
                      className="bg-yellow-500 rounded-full py-16 px-32 text-center whitespace-nowrap"
                      href={DOWNLOAD_URL}
                      target="_blank"
                    >
                      資料ダウンロード
                    </Link>
                  </li>

                  <li>
                    <Link
                      className="bg-yellow-500 rounded-full py-16 px-32 text-center whitespace-nowrap"
                      href={CONTACT_URL}
                      target="_blank"
                    >
                      お問い合わせ
                    </Link>
                  </li>
                </ul>
              </nav>
            )}
          </header>
          <main>
            <section className="relative bg-white h-[64vh] laptop:h-[860px] overflow-hidden">
              <div>
                <div className="absolute left-1/2 top-[6%] laptop:top-[96px] transform -translate-x-1/2 flex flex-col items-center z-10 laptop:space-y-[190px] space-y-48 w-full px-[24px]">
                  <div className="text-center laptop:space-y-[48px] w-full space-y-24">
                    <AnimeFadeIn>
                      <h1 className="flex justify-center w-full">
                        <img
                          className="laptop:w-[587px] small:max-w-[400px] w-full"
                          src="/imgs/maker_lp/title.svg"
                          alt="設計者のさがすを軸にスペックインを加速する"
                        />
                      </h1>
                    </AnimeFadeIn>
                    <AnimeFadeIn delay={0.2}>
                      <p className="text-[18px] laptop:text-[24px] font-bold">
                        あなたの建材をわたしたちの
                        <br className="laptop:hidden" />
                        データベースに
                        <br />
                        登録しませんか？
                      </p>
                    </AnimeFadeIn>
                  </div>
                  <Link
                    className="bg-black rounded-full text-white py-20 px-[20px] laptop:px-48 text-[19px] laptop:text-[22px] font-bold text-center whitespace-nowrap"
                    href={DOWNLOAD_URL}
                    target="_blank"
                  >
                    まずは資料ダウンロード
                  </Link>
                </div>

                {isComplete && (
                  <div
                    className={[
                      "absolute inset-[-240px] bottom-0 laptop:inset-[-32px]",
                      "after:absolute after:bottom-0 after:inset-x-0 after:content-[''] after:w-full after:h-[100px]",
                      "after:bg-gradient-to-b after:from-transparent after:via-white after:to-white",
                    ].join(" ")}
                  >
                    <div className="grid grid-rows-[repeat(24,minmax(0,1fr))] grid-cols-4 gap-24 w-full h-full">
                      {/* 左と上から順番 */}
                      <div className="[&>*]:w-full [&>*]:h-full row-start-[10] row-end-[17] col-start-1 col-end-2">
                        <CardItem imgSrc={TOP_IMAGES[0]} delay={0} />
                      </div>
                      <div className="[&>*]:w-full [&>*]:h-full row-start-[17] row-end-[25] col-start-1 col-end-2">
                        <CardItem imgSrc={TOP_IMAGES[1]} delay={0.1} />
                      </div>
                      <div className="[&>*]:w-full [&>*]:h-full col-start-2 col-end-3 row-start-[14] row-end-[25]">
                        <CardItem imgSrc={TOP_IMAGES[2]} delay={0.2} />
                      </div>
                      <div className="[&>*]:w-full [&>*]:h-full col-start-3 col-end-4 row-start-[15] row-end-[21]">
                        <CardItem imgSrc={TOP_IMAGES[3]} delay={0.3} />
                      </div>
                      <div className="[&>*]:w-full [&>*]:h-full col-start-3 col-end-4 row-start-[21] row-end-[25]">
                        <CardItem imgSrc={TOP_IMAGES[4]} delay={0.4} />
                      </div>
                      <div className="[&>*]:w-full [&>*]:h-full col-start-4 col-end-5 row-start-[9] row-end-[16]">
                        <CardItem imgSrc={TOP_IMAGES[5]} delay={0.4} />
                      </div>
                      <div className="[&>*]:w-full [&>*]:h-full col-start-4 col-end-5 row-start-[16] row-end-[25]">
                        <CardItem imgSrc={TOP_IMAGES[6]} delay={0.5} />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </section>

            {isComplete && <MakerLogoCarousel />}

            <section id="about" className="bg-white">
              <div className="max-w-[1280px] mx-auto px-16 py-80 laptop:py-[140px] laptop:space-y-80 laptop:px-32 laptop:py-[140px] ">
                <div className="space-y-[60px] laptop:space-y-80 bg-no-repeat laptop:bg-[url('/imgs/maker_lp/bg_about.webp')] laptop:bg-right-top">
                  <div className="space-y-[40px] laptop:space-y-48">
                    <SectionTitle titleEn="ABOUT" titleJP="TECTURE" />
                    <AnimeFadeIn>
                      <p className="text-center font-bold text-[24px] laptop:text-[35px] leading-tight">
                        TECTUREは
                        <br className="laptop:hidden" />
                        建材の検索・情報収集ができる
                        <br />
                        プラットフォームです。
                      </p>
                    </AnimeFadeIn>
                  </div>

                  <div className="flex flex-col items-center gap-48 laptop:gap-12 laptop:flex-row">
                    <img
                      className="small:w-[300px]"
                      src="/imgs/maker_lp/image_site_PC.webp"
                      alt="TECTUREのPCサイト"
                    />

                    <img
                      className="small:w-[145px]"
                      src="/imgs/maker_lp/image_site_SP.webp"
                      alt="TECTUREのスマホサイト"
                    />
                  </div>
                </div>
              </div>
            </section>

            <section className="bg-[#fafafa]">
              <div className="max-w-[1280px] mx-auto px-16 space-y-32 laptop:space-y-80 pb-80 laptop:pb-0 laptop:px-32 pt-[40px] laptop:pt-[80px] ">
                <div className="text-center font-bold flex flex-col items-center gap-12">
                  <AnimeFadeIn>
                    <h2 className="text-[18px] laptop:text-[50px]">
                      こんなお困りごとはございませんか
                    </h2>
                  </AnimeFadeIn>

                  <AnimeFadeIn>
                    <p
                      className={[
                        "flex items-center gap-32 text-[30px] laptop:text-[50px] laptop:gap-24 leading-[120%]",
                        "before:block before:content-[''] before:bg-yellow-500 before:w-[4px] before:h-[56px] laptop:before:h-[64px] before:transform before:rotate-[-20deg] before:rounded-full",
                        "after:block after:content-[''] after:bg-yellow-500 after:w-[4px] after:h-[56px] laptop:after:h-[64px] after:transform after:rotate-[20deg] after:rounded-full",
                      ].join(" ")}
                    >
                      マーケティング
                      <br className="laptop:hidden" />
                      担当者の声
                    </p>
                  </AnimeFadeIn>
                </div>
                <div className="grid gap-32 laptop:grid-cols-3 items-center justify-center max-w-[980px] mx-auto">
                  {[
                    {
                      src: "/imgs/maker_lp/image_people01.webp",
                      alt: "もっと多くの人に自社製品を知ってもらいたいマーケティング担当者",
                    },
                    {
                      src: "/imgs/maker_lp/image_people02.webp",
                      alt: "納品事例写真をもっと集めたい担当者",
                    },
                    {
                      src: "/imgs/maker_lp/image_people03.webp",
                      alt: "サンプル請求の管理・配送の手間を抑えたい",
                    },
                  ].map(({ src, alt }) => (
                    <div className="flex justify-center" key={src}>
                      <img src={src} alt={alt} />
                    </div>
                  ))}
                </div>
              </div>
            </section>

            <section id="merits" className="bg-white">
              <div className="py-[80px] laptop:py-160">
                <div className="bg-no-repeat bg-right-top">
                  <div className="space-y-[20px] laptop:space-y-80">
                    <SectionTitle titleEn="MERITS" titleJP="導入メリット" />

                    <div className="space-y-[80px] laptop:space-y-120">
                      <AnimeFadeIn>
                        <section
                          className={[
                            "relative px-[30px]",
                            "laptop:before:content-[''] before:block before:absolute before:left-[calc(50%+480px)] before:top-[calc(0%-200px)] before:w-[170px] before:h-[593px] before:bg-[url('/imgs/maker_lp/bg_benefit.svg')] before:bg-no-repeat before:transform before:-translate-x-1/2",
                            "laptop:after:content-[''] after:block after:absolute after:left-[calc(50%-530px)] after:top-[calc(0%+110px)] after:w-[227px] after:h-[571px] after:bg-[url('/imgs/maker_lp/bg_melit.svg')] after:bg-no-repeat after:transform after:-translate-x-1/2",
                            "small:before:hidden small:after:hidden",
                          ].join(" ")}
                        >
                          <div className="relative flex flex-col justify-center gap-[20px] laptop:gap-[40px] z-10 laptop:flex-row">
                            <img
                              className="small:block small:mx-auto small:w-[72%]"
                              src="/imgs/maker_lp/melit_illust_1.svg"
                              alt="TECTUREを利用してメーカー横断で検索をする設計士のイラスト"
                            />
                            <div className="space-y-16">
                              <h2>
                                <img
                                  className="w-full small:max-w-[314px] laptop:w-[547px] max-w-none"
                                  src="/imgs/maker_lp/title_user_50000.svg"
                                  alt="設計者約50000人"
                                />
                              </h2>

                              <h3 className="font-bold text-[24px] laptop:text-[40px]">
                                新たな客層にむけて
                                <br className="laptop:hidden" />
                                アプローチ
                              </h3>

                              <p className="text-[16px] font-bold leading-loose">
                                5万人以上のユーザーが貴社の商品に触れる機会があります。
                                <br />
                                ECサイトのようにメーカー横断の詳細検索で
                                <br />
                                貴社のことをまだ知らないユーザーにも製品情報が目に留まります。
                              </p>
                            </div>
                          </div>
                        </section>
                      </AnimeFadeIn>

                      <AnimeFadeIn>
                        <section className="relative flex flex-col jutify-center small:gap-[40px] laptop:flex-row z-10">
                          <div className="relative flex-1 w-full laptop:mr-[64px] flex items-center laptop:justify-center">
                            <div className="flex items-start laptop:items-center flex-col w-full">
                              <div className="space-y-16 laptop:space-y-32 pl-[30px] pr-[30px] laptop:pl-24 laptop:pr-0 small:w-full">
                                <div className="flex flex-col gap-[24px] laptop:flex-row laptop:items-center">
                                  <div className="flex-1">
                                    <img
                                      className="w-full h-auto small:block small:mx-auto small:w-[72%]"
                                      src="/imgs/maker_lp/melit_illust_2.svg"
                                      alt="プロダクトを登録すると利用事例写真にひもづくイラスト"
                                    />
                                  </div>
                                  <h2 className="flex-1 font-bold text-[24px] laptop:text-[40px]">
                                    <img
                                      className="w-full h-auto small:max-w-[227px] laptop:max-w-[341px] max-w-none"
                                      src="/imgs/maker_lp/title_post_53000.svg"
                                      alt="施工事例写真約53000点"
                                    />
                                  </h2>
                                </div>

                                <div className="space-y-16 laptop:space-y-24">
                                  <h3 className="text-[24px] laptop:text-[32px] font-bold leading-tight">
                                    自社製品の利用事例が
                                    <br className="laptop:hidden" />
                                    どんどん集まる。
                                  </h3>
                                  <p className="text-[16px] font-bold">
                                    設計者によって投稿される事例写真には製品情報の写真との紐づけが可能です。
                                    <br />
                                    自社で持っている施工事例写真が少ない場合でも、TECTUREに製品を登録することで、
                                    <br />
                                    有効な施工事例写真がどんどん集まっていきます。
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="flex-1 w-full small:pl-[30px]">
                            <SampleImageFadeCaoursel
                              srcs={[
                                "/imgs/maker_lp/image_melit_photo_01_01.webp",
                                "/imgs/maker_lp/image_melit_photo_01_02.webp",
                                "/imgs/maker_lp/image_melit_photo_01_03.webp",
                              ]}
                            />
                          </div>
                        </section>
                      </AnimeFadeIn>

                      <AnimeFadeIn>
                        <section
                          className={[
                            "relative flex flex-col jutify-center small:gap-[40px] laptop:flex-row",
                            "laptop:before:content-[''] before:block before:absolute before:left-[calc(50%-270px)] before:top-[calc(0%-148px)] before:w-[947px] before:h-[231px] before:bg-[url('/imgs/maker_lp/bg_solution.svg')] before:bg-no-repeat before:transform before:-translate-x-1/2",
                            "small:before:hidden small:after:hidden",
                          ].join(" ")}
                        >
                          <div className="flex-1 w-full laptop:mr-[64px] relative z-20 flex items-center laptop:justify-center">
                            <div className="flex items-start laptop:items-center flex-col w-full">
                              <div className="space-y-16 laptop:space-y-32 pl-[30px] pr-[30px] laptop:pl-24 laptop:pr-0 small:w-full max-w-[648px] w-full">
                                <div className="flex flex-col laptop:items-center gap-24 laptop:flex-row">
                                  <div className="flex-1">
                                    <img
                                      className="small:block small:mx-auto small:w-[72%]"
                                      src="/imgs/maker_lp/melit_illust_3.svg"
                                      alt="プロダクトを登録すると利用事例写真にひもづくイラスト"
                                    />
                                  </div>
                                  <h2 className="flex-1 font-bold text-[24px] laptop:text-[40px]">
                                    <img
                                      className="w-full small:max-w-[265px] laptop:max-w-[363px] max-w-none"
                                      src="/imgs/maker_lp/title_deliver.svg"
                                      alt="サンプル請求最短即日発送"
                                    />
                                  </h2>
                                </div>

                                <div className="space-y-16 laptop:space-y-24">
                                  <h3 className="text-[24px] laptop:text-[32px] font-bold">
                                    サンプルは倉庫で管理、一括送付
                                  </h3>
                                  <p className="text-[16px] font-bold">
                                    オプションでサンプルをTECTURE倉庫に預け、管理・配送の手間を抑えることができます。貴社に変わってTECTUREが保管・受注・出荷業務を代行します。顧客情報はWEB上で確認・ダウンロードが可能です。
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="flex-1 w-full z-10 small:pl-[30px]">
                            <SampleImageFadeCaoursel
                              srcs={[
                                "/imgs/maker_lp/image_melit_photo_02_01.webp",
                                "/imgs/maker_lp/image_melit_photo_02_03.webp",
                              ]}
                            />
                          </div>
                        </section>
                      </AnimeFadeIn>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section id="features" className="bg-[#fafafa]">
              <div className="max-w-[1280px] mx-auto py-80 px-[30px] space-y-48 laptop:space-y-80 laptop:px-32 laptop:py-[140px] ">
                <SectionTitle titleEn="FEATURES" titleJP="主な機能" />
                <FunctionItems />
              </div>
            </section>

            <section id="promotion" className="bg-white">
              <div className="mx-auto max-w-[1280px] pt-[80px] pb-[140px] px-[20px] space-y-[40px] laptop:space-y-80 laptop:py-[180px]">
                <SectionTitle titleEn="PROMOTION" titleJP="プロモーション" />
                <div className="space-y-40 laptop:space-y-80">
                  <div className="flex flex-col justify-center items-center gap-48 laptop:flex-row">
                    <img
                      src="/imgs/maker_lp/image_feature_SP.webp"
                      alt="国内最大の空間デザインメディア TECTURE MAG"
                    />

                    <div className="flex flex-col items-center gap-[20px] laptop:gap-48">
                      <h2>
                        <Link href={MAG_URL} target="_blank">
                          <img
                            className="small:w-[246px]"
                            src="/imgs/maker_lp/logo_mag-tecture.svg"
                            alt="国内最大の空間デザインメディアTECTURE MAGのロゴ"
                          />
                        </Link>
                      </h2>
                      <p className="font-bold text-[18px] laptop:text-[26px] text-center">
                        国内最大の空間デザインメディア
                        <br />
                        TECTURE MAG
                        <br /> （約25万ユーザ）での発信
                      </p>
                    </div>
                  </div>

                  <hr />

                  <div className="flex flex-col justify-center items-center gap-[40px] laptop:gap-64 laptop:flex-row">
                    <p className="text-center font-bold text-[18px] laptop:text-[30px]">
                      TECTURE公式SNS
                      <br />
                      （約10万フォロワー）での発信
                    </p>
                    <div className="flex flex-row gap-[30px] laptop:gap-40">
                      {[
                        {
                          src: "/imgs/maker_lp/icon_instagram.svg",
                          alt: "TECTUREのInstagramアカウント",
                          href: INSTAGRAM_URL,
                        },
                        {
                          src: "/imgs/maker_lp/icon_x.svg",
                          alt: "TECTUREのXアカウント",
                          href: X_URL,
                        },
                        {
                          src: "/imgs/maker_lp/icon_facebook.svg",
                          alt: "TECTUREのFacebookアカウント",
                          href: FACEBOOK_URL,
                        },
                      ].map((value) => (
                        <SnsItem key={value.src} {...value} />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section className="bg-yellow-500">
              <div className="max-w-[1280px] mx-auto pt-[40px] pb-[64px] px-[30px] laptop:px-32 laptop:py-[100px] ">
                <div className="flex flex-col gap-[40px] laptop:justify-center laptop:flex-row laptop:gap-160">
                  <div className="laptop:max-w-[460px] space-y-[20px] laptop:space-y-40 leading-loose">
                    <p className="text-[14px] laptop:text-[20px] font-bold">
                      サービスや機能のご案内、プラン等、ご不明点についてご説明いたします。お気軽にご相談ください。
                    </p>

                    <div className="flex flex-col gap-20 items-start">
                      <Link
                        className="inline-block rounded-full text-white text-center py-16 px-48 font-bold"
                        style={{
                          backgroundColor: theme.colors.gray[950],
                          fontSize: 20,
                        }}
                        href={CONTACT_URL}
                        target="_blank"
                      >
                        お問い合わせ
                      </Link>

                      <Link
                        className="inline-block rounded-full text-white text-center py-16 px-48 font-bold"
                        style={{
                          backgroundColor: theme.colors.gray[950],
                          fontSize: 20,
                        }}
                        href={ONLINE_MTG_URL}
                        target="_blank"
                      >
                        オンライン相談の予約
                      </Link>
                    </div>
                  </div>

                  <div className="laptop:max-w-[460px] space-y-[20px] laptop:space-y-40 leading-loose">
                    <p className="text-[14px] laptop:text-[20px] font-bold">
                      TECTUREのサービス概要をまとめたPDF資料です。導入のご検討にお役立てください。
                    </p>

                    <div className="flex flex-col items-start">
                      <Link
                        className="inline-block rounded-full text-white text-center py-16 px-48 font-bold"
                        style={{
                          backgroundColor: theme.colors.gray[950],
                          fontSize: 20,
                        }}
                        href={DOWNLOAD_URL}
                        target="_blank"
                      >
                        資料ダウンロード
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </main>
        </div>
      </NoHeaderLayout>
    </>
  );
};

export default AdMakerPage;
